import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components/macro";
import { rgba } from "polished";
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl";

import { SUGGESTIONS } from "../SearchMap/suggestions";
import Address from "../SearchMap/Address";
import RangeSlider from "../../components/RangeSlider";
import Checkbox from "../../components/Checkbox";
import FloatingInput from "../../components/FloatingInput";
import Button from "../../components/Button";
import BrandButton from "./BrandButton";
import Icon from "../../components/Icon";
import Loading from "../../components/Loading";
import { CURRENCY_FORMAT } from "../../utils/formatters";
import { MAX_PRICE } from "./brand";
import {
  ONLINE_TYPES,
  ON_SITE_TYPES,
  initialFilters,
  isLocationTypeActive,
  toggleLocationType,
} from "./filters";

const Wrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 11;
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0.5rem 1rem 1rem;

  @media (${props => props.theme.tabletScreen}) {
    top: 5rem;
    padding-bottom: 1.25rem;
  }
`;

/* One translucent panel holding the term, the place and the filter toggle, so
 * the controls read as a single search surface over the background image. */
const Panel = styled.div`
  border-radius: ${props => props.theme.borderRadiusMedium};
  background-color: ${props => rgba(props.theme.white, 0.92)};
  backdrop-filter: blur(10px);
  box-shadow: ${props => props.theme.boxShadowLifted};
  padding: 0.5rem;

  @media (${props => props.theme.tabletScreen}) {
    padding: 0.75rem;
  }
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const TermField = styled(FloatingInput)`
  position: static !important;
  flex: 1 1 100%;
  max-width: 100% !important;
  box-shadow: none;
  padding: 0 !important;

  @media (${props => props.theme.tabletScreen}) {
    flex: 2 1 260px;
  }
`;

/* Address renders a FloatingContainer capped at 300/600px; inside a filter
 * column it should simply fill the width it is given. */
const Place = styled.div`
  & > div {
    max-width: 100%;
  }
`;

/* The place is edited inside the filter panel, so while that panel is closed
 * this line is the only thing telling the visitor where they are searching. */
const PositionDisplay = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-right: auto;
  padding: 0.15rem 0.5rem 0.15rem 0.25rem;
  border: 0;
  border-radius: 2rem;
  background: none;
  appearance: none;
  cursor: pointer;
  font-family: ${props => props.theme.fontFamilyBase};
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.gray700};
  min-width: 0;
  max-width: 11rem;
  transition: color 0.25s;

  @media (${props => props.theme.tabletScreen}) {
    max-width: 16rem;
  }

  &:hover,
  &:focus-visible {
    outline: none;
    color: ${props => props.theme.dark};
    text-decoration: underline;
  }
`;

const PositionName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex: 1 1 100%;

  @media (${props => props.theme.tabletScreen}) {
    flex: 0 0 auto;
    margin-left: auto;
  }
`;

const ResultsDisplay = styled.span`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.dark};
  white-space: nowrap;
  padding: 0 0.25rem;

  & > div {
    padding: 0;
    margin: 0;
  }
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${props => props.theme.gray200};
`;

const Filter = styled.section`
  flex: 1 1 240px;
  min-width: 0;
`;

const FilterLabel = styled.p`
  margin-bottom: 0.25rem;
  font-family: ${props => props.theme.fontFamilyBrand};
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.gray700};
`;

const Checkboxes = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex: 1 1 100%;
`;

const SearchBar = ({
  intl,
  isLoading,
  term,
  totalCount,
  hasSearched,
  filters,
  setFilters,
  editFilters,
  setEditFilters,
  address,
  center,
  onAddressSelect,
  onAddressClear,
  onSubmit,
}) => {
  const maxPrice = (filters.maxPrice && parseInt(filters.maxPrice, 10)) || MAX_PRICE;
  const ToggleButton = editFilters ? BrandButton : Button;

  /* Address names the device position "Meine Position" when it geolocates, so
   * showing the address covers that case without a special branch. */
  const hasCenter = Boolean(center && center.lat && center.lng);
  const position =
    address ||
    intl.formatMessage({ id: hasCenter ? "pages.Search.yourPosition" : "pages.Search.noPosition" });

  const toggleLocation = group =>
    setFilters(current => ({
      ...current,
      locationTypes: toggleLocationType(current.locationTypes, group),
    }));

  return (
    <Wrapper>
      <Panel>
        <Row>
          <TermField
            name="search"
            defaultValue={term}
            busy={isLoading}
            onSubmit={onSubmit}
            defaultExpanded
            placeholder={intl.formatMessage({ id: "pages.PampersSearch.placeholder" })}
            suggestions={SUGGESTIONS}
          />
          <Meta>
            {!editFilters && (
              <PositionDisplay type="button" onClick={() => setEditFilters(true)}>
                <Icon name="location-dot" size="small" />
                <PositionName>{position}</PositionName>
              </PositionDisplay>
            )}
            {hasSearched && (
              <ResultsDisplay>
                {isLoading ? (
                  <Loading />
                ) : (
                  <FormattedMessage id="pages.SearchMap.results" values={{ count: totalCount }} />
                )}
              </ResultsDisplay>
            )}
            <ToggleButton
              color="default"
              onClick={() => setEditFilters(!editFilters)}
              aria-expanded={editFilters}
              style={{ opacity: 1 }}
              round
            >
              <Icon name="Filter" />
              <FormattedMessage id="pages.Search.filterSelect" />
            </ToggleButton>
          </Meta>
        </Row>
        {editFilters && (
          <Filters>
            <Filter>
              <FilterLabel>
                <FormattedMessage id="pages.Search.filters.location" />
              </FilterLabel>
              <Place>
                <Address
                  address={address}
                  onSelect={(selected, { lat, lng }) => onAddressSelect(selected, lat, lng)}
                  onClear={onAddressClear}
                  defaultExpanded
                  autoFocus={false}
                />
              </Place>
            </Filter>
            <Filter>
              <FilterLabel>
                <FormattedMessage id="pages.Search.filters.price" />
              </FilterLabel>
              <RangeSlider
                onChange={value =>
                  setFilters(current => ({
                    ...current,
                    maxPrice: value >= MAX_PRICE ? null : value,
                  }))
                }
                from={0}
                to={MAX_PRICE}
                format={v => (
                  <span>
                    <span>{v >= MAX_PRICE ? "> " : "max "}</span>
                    <FormattedNumber value={v} {...CURRENCY_FORMAT} maximumFractionDigits={0} />
                  </span>
                )}
                defaultValue={maxPrice}
              />
            </Filter>
            <Filter>
              <FilterLabel>
                <FormattedMessage id="pages.Search.filters.type" />
              </FilterLabel>
              <Checkboxes>
                <Checkbox
                  active={isLocationTypeActive(filters.locationTypes, ONLINE_TYPES)}
                  onClick={() => toggleLocation(ONLINE_TYPES)}
                >
                  <Icon name="display" size="small" />
                  <FormattedMessage id="course.attributes.locationType.online" />
                </Checkbox>
                <Checkbox
                  active={isLocationTypeActive(filters.locationTypes, ON_SITE_TYPES)}
                  onClick={() => toggleLocation(ON_SITE_TYPES)}
                >
                  <Icon name="location-dot" size="small" />
                  <FormattedMessage id="course.attributes.locationType.onSite" />
                </Checkbox>
              </Checkboxes>
            </Filter>
            <FilterActions>
              <Button
                size="small"
                onClick={() => setFilters(initialFilters())}
                color="dark"
                outline
                round
              >
                <FormattedMessage id="actions.reset" />
              </Button>
              <BrandButton onClick={() => setEditFilters(false)} round>
                <FormattedMessage id="actions.apply" />
              </BrandButton>
            </FilterActions>
          </Filters>
        )}
      </Panel>
    </Wrapper>
  );
};

SearchBar.propTypes = {
  intl: PropTypes.object.isRequired,
  isLoading: PropTypes.bool,
  term: PropTypes.string,
  totalCount: PropTypes.number,
  hasSearched: PropTypes.bool,
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
  editFilters: PropTypes.bool,
  setEditFilters: PropTypes.func.isRequired,
  address: PropTypes.string,
  center: PropTypes.object,
  onAddressSelect: PropTypes.func.isRequired,
  onAddressClear: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

SearchBar.defaultProps = {
  isLoading: false,
  term: "",
  totalCount: 0,
  hasSearched: false,
  editFilters: false,
  address: null,
  center: null,
};

export default injectIntl(SearchBar);
